/**
 * Catalogue d'icônes de catégories (clés neutres).
 * Mobile : MaterialCommunityIcons via `mci`
 * Web : Lucide via `lucide` (nom de composant)
 */

export type CategoryIconKey =
  | 'tag'
  | 'cart'
  | 'food'
  | 'home'
  | 'car'
  | 'bus'
  | 'fuel'
  | 'gift'
  | 'heart'
  | 'wifi'
  | 'phone'
  | 'fire'
  | 'bolt'
  | 'water'
  | 'cash'
  | 'bank'
  | 'wallet'
  | 'piggy'
  | 'medical'
  | 'school'
  | 'briefcase'
  | 'tools'
  | 'shirt'
  | 'coffee'
  | 'restaurant'
  | 'dumbbell'
  | 'plane'
  | 'paw'
  | 'baby'
  | 'book'
  | 'film'
  | 'music'
  | 'game'
  | 'leaf'
  | 'more'
  | 'receipt'
  | 'percent'
  | 'hand-coin'
  | 'shopping'
  | 'family'
  | 'laundry';

export interface CategoryIconDef {
  key: CategoryIconKey;
  label: string;
  /** Nom MaterialCommunityIcons */
  mci: string;
  /** Nom export Lucide React */
  lucide: string;
}

export const CATEGORY_ICONS: CategoryIconDef[] = [
  { key: 'tag', label: 'Général', mci: 'tag', lucide: 'Tag' },
  { key: 'cart', label: 'Courses', mci: 'cart', lucide: 'ShoppingCart' },
  { key: 'shopping', label: 'Shopping', mci: 'shopping', lucide: 'ShoppingBag' },
  { key: 'food', label: 'Alimentation', mci: 'food-apple', lucide: 'Apple' },
  { key: 'restaurant', label: 'Restaurant', mci: 'silverware-fork-knife', lucide: 'UtensilsCrossed' },
  { key: 'coffee', label: 'Café', mci: 'coffee', lucide: 'Coffee' },
  { key: 'home', label: 'Logement', mci: 'home', lucide: 'Home' },
  { key: 'car', label: 'Voiture', mci: 'car', lucide: 'Car' },
  { key: 'bus', label: 'Transport', mci: 'bus', lucide: 'Bus' },
  { key: 'fuel', label: 'Carburant', mci: 'gas-station', lucide: 'Fuel' },
  { key: 'gift', label: 'Cadeau', mci: 'gift', lucide: 'Gift' },
  { key: 'heart', label: 'Don', mci: 'heart', lucide: 'Heart' },
  { key: 'wifi', label: 'Internet', mci: 'wifi', lucide: 'Wifi' },
  { key: 'phone', label: 'Téléphone', mci: 'cellphone', lucide: 'Smartphone' },
  { key: 'fire', label: 'Gaz', mci: 'fire', lucide: 'Flame' },
  { key: 'bolt', label: 'Électricité', mci: 'flash', lucide: 'Zap' },
  { key: 'water', label: 'Eau', mci: 'water', lucide: 'Droplets' },
  { key: 'cash', label: 'Argent', mci: 'cash', lucide: 'Banknote' },
  { key: 'bank', label: 'Banque', mci: 'bank', lucide: 'Landmark' },
  { key: 'wallet', label: 'Portefeuille', mci: 'wallet', lucide: 'Wallet' },
  { key: 'piggy', label: 'Épargne', mci: 'piggy-bank', lucide: 'PiggyBank' },
  { key: 'hand-coin', label: 'Prêt', mci: 'hand-coin', lucide: 'Coins' },
  { key: 'receipt', label: 'Facture', mci: 'receipt', lucide: 'Receipt' },
  { key: 'percent', label: 'Impôts', mci: 'percent', lucide: 'Percent' },
  { key: 'medical', label: 'Santé', mci: 'medical-bag', lucide: 'HeartPulse' },
  { key: 'school', label: 'Éducation', mci: 'school', lucide: 'GraduationCap' },
  { key: 'briefcase', label: 'Travail', mci: 'briefcase', lucide: 'Briefcase' },
  { key: 'tools', label: 'Bricolage', mci: 'hammer-wrench', lucide: 'Wrench' },
  { key: 'shirt', label: 'Habillement', mci: 'tshirt-crew', lucide: 'Shirt' },
  { key: 'dumbbell', label: 'Sport', mci: 'dumbbell', lucide: 'Dumbbell' },
  { key: 'plane', label: 'Voyage', mci: 'airplane', lucide: 'Plane' },
  { key: 'paw', label: 'Animaux', mci: 'paw', lucide: 'PawPrint' },
  { key: 'baby', label: 'Enfants', mci: 'baby-carriage', lucide: 'Baby' },
  { key: 'family', label: 'Famille', mci: 'account-group', lucide: 'Users' },
  { key: 'book', label: 'Livres', mci: 'book-open-page-variant', lucide: 'BookOpen' },
  { key: 'film', label: 'Loisirs', mci: 'movie-open', lucide: 'Clapperboard' },
  { key: 'music', label: 'Musique', mci: 'music', lucide: 'Music' },
  { key: 'game', label: 'Jeux', mci: 'gamepad-variant', lucide: 'Gamepad2' },
  { key: 'leaf', label: 'Jardin', mci: 'flower', lucide: 'Leaf' },
  { key: 'laundry', label: 'Entretien', mci: 'washing-machine', lucide: 'Shirt' },
  { key: 'more', label: 'Divers', mci: 'dots-horizontal', lucide: 'MoreHorizontal' },
];

const BY_KEY = new Map(CATEGORY_ICONS.map((i) => [i.key, i]));

export function resolveCategoryIcon(key?: string | null): CategoryIconDef {
  if (key && BY_KEY.has(key as CategoryIconKey)) {
    return BY_KEY.get(key as CategoryIconKey)!;
  }
  return BY_KEY.get('tag')!;
}

/** Suggère une icône à partir du nom (création rapide). */
export function suggestCategoryIcon(nom: string): CategoryIconKey {
  const n = (nom || '').toLowerCase();
  const rules: [RegExp, CategoryIconKey][] = [
    [/don/, 'heart'],
    [/logement|loyer|maison|habitat|assurance habitation|cr[eé]dit immobilier/, 'home'],
    [/cadeau|anniversaire|no[eë]l/, 'gift'],
    [/internet|wifi|t[eé]l[eé]phone|(?<![a-z])mobile(?![a-z])|box/, 'wifi'],
    [/gaz|chauffage/, 'fire'],
    [/[eé]lectr|edf|[eé]nergie/, 'bolt'],
    [/(?<![a-z])eau(?![a-z])/, 'water'],
    [/essence|gazoil|gasoil|carburant|p[eé]age/, 'fuel'],
    [/voiture|auto(?![a-z])|automobile|v[eé]hicule|assurance auto|stationnement|parking/, 'car'],
    [/transport|m[eé]tro|bus|train|sncf/, 'bus'],
    [/course|aliment|supermarch|[eé]picerie/, 'cart'],
    [/boucher|boulang|restaurant|resto/, 'restaurant'],
    [/caf[eé]|snack|bar/, 'coffee'],
    [/habillement|v[eê]tement|mode/, 'shirt'],
    [/sant[eé]|pharma|m[eé]decin|dentiste|mutuelle/, 'medical'],
    [/[eé]cole|[eé]ducation|formation/, 'school'],
    [/salaire|travail|emploi|freelance/, 'briefcase'],
    [/banque|agios|frais banc/, 'bank'],
    [/[eé]pargne|[eé]conom|passif/, 'piggy'],
    [/voyage|vacance|avion|h[oô]tel/, 'plane'],
    [/loisir|cin[eé]|spectacle/, 'film'],
    [/sport|fitness/, 'dumbbell'],
    [/animal|v[eé]t[eé]rin|chien|chat/, 'paw'],
    [/enfant|b[eé]b[eé]|cr[eè]che|garde/, 'baby'],
    [/famille/, 'family'],
    [/bricolage|r[eé]paration|entretien/, 'tools'],
    [/jardin|plante/, 'leaf'],
    [/jeu|gaming|console/, 'game'],
    [/abonnement|musique|spotify|netflix|streaming/, 'music'],
    [/livre|librairie/, 'book'],
    [/shopping|achat/, 'shopping'],
    [/imp[oô]t|taxe/, 'percent'],
    [/facture/, 'receipt'],
    [/pr[eê]t|emprunt/, 'hand-coin'],
    [/aide|allocation|rembours|virement|transfert/, 'cash'],
    [/autre|divers|babioles/, 'more'],
  ];
  for (const [re, key] of rules) {
    if (re.test(n)) return key;
  }
  return 'tag';
}
